/**
 * Утилиты для обработки ошибок API
 * ✅ Переиспользуемая логика обработки ошибок с поддержкой i18next
 * ✅ Поддержка toast-уведомлений для сетевых ошибок
 */

import { isAxiosError, AxiosError } from 'axios';
import { message } from 'antd';
import type { TFunction } from 'i18next';
import type { MessageInstance } from 'antd/es/message/interface';
import type { NotificationInstance } from 'antd/es/notification/interface';

export interface ErrorHandlerOptions {
  /**
   * Функция перевода из i18next
   */
  t: TFunction;
  /**
   * Namespace для переводов ошибок (например: 'cable', 'inventory')
   */
  namespace?: string;
  /**
   * Ключ для fallback ошибки (по умолчанию: 'common.error')
   */
  fallbackKey?: string;
  /**
   * Маппинг кодов ошибок на ключи переводов
   * Формат: { 'error_code': 'translation.key' }
   */
  errorCodeMap?: Record<string, string>;
  /**
   * Message API из App.useApp() для поддержки динамической темы
   * Если не передан, используется статический message (не рекомендуется)
   */
  messageApi?: MessageInstance;
  /**
   * Notification API из App.useApp() для показа toast-уведомлений
   * Используется для сетевых ошибок и критичных операций
   */
  notificationApi?: NotificationInstance;
  /**
   * Использовать notification вместо message для всех ошибок
   * По умолчанию: false (только для сетевых ошибок)
   */
  useNotificationForAll?: boolean;
}

/**
 * Обрабатывает ошибку API и показывает сообщение пользователю
 * 
 * @param error - Ошибка (обычно из axios)
 * @param options - Опции обработки ошибки
 * 
 * @example
 * ```typescript
 * const { t } = useTranslation(['translation', 'cable']);
 * 
 * onError: (error) => {
 *   handleApiError(error, {
 *     t,
 *     namespace: 'cable',
 *     errorCodeMap: {
 *       'line_has_dependencies': 'cable:lines.errors.dependencies',
 *       'wire_in_use': 'cable:lines.errors.wireInUse',
 *     }
 *   });
 * }
 * ```
 */
export function handleApiError(
  error: unknown,
  options: ErrorHandlerOptions,
): void {
  const {
    t,
    namespace,
    fallbackKey = 'common.error',
    errorCodeMap = {},
    messageApi,
    notificationApi,
    useNotificationForAll = false,
  } = options;

  // Логирование ошибки для отладки
  if (import.meta.env.DEV) {
    console.error('[handleApiError] Error details:', {
      error,
      namespace,
      fallbackKey,
      isAxiosError: isAxiosError(error),
      response: isAxiosError(error) ? error.response?.data : undefined,
      status: isAxiosError(error) ? error.response?.status : undefined,
    });
  }

  // Используем messageApi из App.useApp() если передан, иначе fallback на статический message
  const messageInstance = messageApi || message;

  if (isAxiosError(error)) {
    const code = error.response?.data?.error;
    const status = error.response?.status;
    const isNetworkError = !error.response; // Сетевая ошибка (нет ответа от сервера)

    // Для сетевых ошибок используем notification (toast)
    if (isNetworkError && notificationApi) {
      notificationApi.error({
        message: t('common.errors.network.title', { defaultValue: 'Ошибка сети' }),
        description: t('common.errors.network.description', {
          defaultValue: 'Не удалось подключиться к серверу. Проверьте подключение к интернету.',
        }),
        duration: 5,
        placement: 'bottomRight',
      });
      return;
    }

    // Проверяем маппинг кодов ошибок
    if (code && errorCodeMap[code]) {
      const errorMessage = t(errorCodeMap[code]);
      if (useNotificationForAll && notificationApi) {
        notificationApi.error({
          message: t('common.error', { defaultValue: 'Ошибка' }),
          description: errorMessage,
          duration: 4,
        });
      } else {
        messageInstance.error(errorMessage);
      }
      return;
    }

    // Проверяем namespace-specific ошибки (например: 'cable:lines.errors.{code}')
    if (code && namespace) {
      const namespaceKey = `${namespace}:errors.${code}`;
      // Пытаемся найти перевод, если не найден - используем fallback.
      // defaultValue: '' — единственная надёжная проверка «перевода нет»: сравнение
      // с самим ключом не работает, i18next возвращает ключ БЕЗ namespace-префикса
      // ('errors.<code>' вместо 'tasks:errors.<code>'), поэтому условие
      // translation !== namespaceKey было истинным ВСЕГДА — оператор получал сырой
      // ключ вместо сообщения, а когда code приходил текстом ошибки БД, то ещё и
      // имена таблиц с колонками (дефект F31 на создании задачи).
      const translation = t(namespaceKey, { defaultValue: '' });
      if (translation) {
        if (useNotificationForAll && notificationApi) {
          notificationApi.error({
            message: t('common.error', { defaultValue: 'Ошибка' }),
            description: translation,
            duration: 4,
          });
        } else {
          messageInstance.error(translation);
        }
        return;
      }
    }

    // Обработка специфичных HTTP статусов
    //
    // ⚠️ Конкретная причина СЕРВЕРА побеждает общую фразу статуса. Прежде ветки 409 и 403
    // показывали «Конфликт данных» / «Доступ запрещен» и выходили ДО проверки
    // `data.message` — то есть действенное объяснение сервера («Баланс этого клиента
    // управляется биллингом, правьте там», `customer_handler.go:879`, статус 409)
    // отбрасывалось во ВСЕХ 73 файлах, зовущих этот обработчик. Оператор видел «Конфликт
    // данных» и повторял операцию, которая не могла пройти: отказ был, объяснения не было.
    // Ветка 5xx ниже уже делала именно так — здесь то же правило, а не новое.
    const serverMessage =
      typeof error.response?.data?.message === 'string' && error.response.data.message.trim()
        ? error.response.data.message
        : undefined;

    if (status === 409) {
      const errorMessage =
        serverMessage ?? t('common.errors.conflict', { defaultValue: 'Конфликт данных' });
      if (useNotificationForAll && notificationApi) {
        notificationApi.warning({
          message: t('common.error', { defaultValue: 'Ошибка' }),
          description: errorMessage,
          duration: 4,
        });
      } else {
        messageInstance.error(errorMessage);
      }
      return;
    }

    if (status === 403) {
      const errorMessage =
        serverMessage ?? t('common.errors.forbidden', { defaultValue: 'Доступ запрещен' });
      if (useNotificationForAll && notificationApi) {
        notificationApi.error({
          message: t('common.error', { defaultValue: 'Ошибка' }),
          description: errorMessage,
          duration: 4,
        });
      } else {
        messageInstance.error(errorMessage);
      }
      return;
    }

    if (status === 401) {
      // 401 обрабатывается отдельно в interceptor, здесь не показываем
      return;
    }

    // Серверные ошибки (5xx) - показываем через notification
    if (status && status >= 500 && status < 600 && notificationApi) {
      notificationApi.error({
        message: t('common.errors.server.title', { defaultValue: 'Ошибка сервера' }),
        description: error.response?.data?.message || 
          t('common.errors.server.description', { defaultValue: 'Произошла ошибка на сервере. Попробуйте позже.' }),
        duration: 5,
        placement: 'bottomRight',
      });
      return;
    }

    // Используем сообщение из ответа, если есть
    if (error.response?.data?.message) {
      if (useNotificationForAll && notificationApi) {
        notificationApi.error({
          message: t('common.error', { defaultValue: 'Ошибка' }),
          description: error.response.data.message,
          duration: 4,
        });
      } else {
        messageInstance.error(error.response.data.message);
      }
      return;
    }
  }

  // Fallback на общую ошибку.
  // ⚠️⚠️ `defaultValue` здесь ОБЯЗАТЕЛЕН, и это не перестраховка. Умолчание параметра —
  // `common.error` (см. сигнатуру выше), а этот путь не резолвится НИ В ОДНОМ из 33
  // словарей ни в ru, ни в en (замерено ц.386 обходом всех namespace обеих локалей).
  // Из 174 вызовов `handleApiError` **23** не передают `fallbackKey` и попадают сюда —
  // без защиты оператор в этой ветке читал бы `common.error` вместо текста.
  // Самоулика лежала в трёх строках ниже: там тот же ключ уже вызывался С защитой.
  const fallbackMessage = t(fallbackKey, { defaultValue: 'Ошибка' });
  if (useNotificationForAll && notificationApi) {
    notificationApi.error({
      message: t('common.error', { defaultValue: 'Ошибка' }),
      description: fallbackMessage,
      duration: 4,
    });
  } else {
    messageInstance.error(fallbackMessage);
  }
}

/**
 * Создает обработчик ошибок для конкретного namespace
 * 
 * @example
 * ```typescript
 * const { t } = useTranslation(['translation', 'inventory']);
 * const handleInventoryError = createErrorHandler({
 *   t,
 *   namespace: 'inventory',
 *   errorCodeMap: {
 *     'token_conflict': 'inventory:group.messages.token_conflict',
 *   }
 * });
 * 
 * onError: (error) => handleInventoryError(error, 'inventory:modals.receipt.error');
 * ```
 */
export function createErrorHandler(options: ErrorHandlerOptions) {
  return (error: unknown, fallbackKey?: string) => {
    handleApiError(error, {
      ...options,
      fallbackKey: fallbackKey || options.fallbackKey,
    });
  };
}

